import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';

const TrendChart = ({ points }) => {
  if (points.length < 2) {
    return (
      <div className="text-center py-10 text-gray-600 italic text-xs bg-black/20 rounded-xl border border-gray-850">
        Re-run this site's audit later to see score progression over time.
      </div>
    );
  }

  const w = 720;
  const h = 220;
  const pad = { l: 44, r: 16, t: 20, b: 32 };
  const values = points.map((p) => p.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 100);
  const range = max - min || 1;

  const x = (i) => pad.l + (i * (w - pad.l - pad.r)) / Math.max(points.length - 1, 1);
  const y = (v) => pad.t + (h - pad.t - pad.b) * (1 - (v - min) / range);

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`).join(' ');
  const areaPath = `${path} L ${x(points.length - 1).toFixed(1)} ${h - pad.b} L ${x(0).toFixed(1)} ${h - pad.b} Z`;

  const gridLines = [0, 25, 50, 75, 100].filter((v) => v >= min && v <= max);

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <svg width={w} height={h} className="min-w-full">
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {gridLines.map((v) => (
          <g key={v}>
            <line x1={pad.l} y1={y(v)} x2={w - pad.r} y2={y(v)} stroke="#1f2937" strokeWidth="1" strokeDasharray="4 4" />
            <text x={pad.l - 8} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#6b7280" className="font-mono">
              {v}
            </text>
          </g>
        ))}

        <path d={areaPath} fill="url(#trendFill)" />
        <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {points.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.value)} r="4" fill="#0b1220" stroke="#60a5fa" strokeWidth="2" />
            <text x={x(i)} y={y(p.value) - 10} textAnchor="middle" fontSize="9" fill="#93c5fd" className="font-mono font-bold">
              {p.value}
            </text>
            <text x={x(i)} y={h - 10} textAnchor="middle" fontSize="8" fill="#6b7280" className="font-mono">
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const TrendsPanel = ({ project, apiFetch }) => {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!project?.id) return;
    setHistory(null);
    setError(null);
    apiFetch(`/seo/projects/${project.id}/history`)
      .then((data) => { if (!cancelled) setHistory(data || []); })
      .catch((err) => { if (!cancelled) { setError(err.message); setHistory([]); } });
    return () => { cancelled = true; };
  }, [project?.id, apiFetch]);

  const points = (history || []).map((entry) => {
    const raw = entry.date;
    let label = 'Now';
    if (raw) {
      try {
        const d = new Date(raw);
        if (!isNaN(d.getTime())) label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } catch (e) { /* keep 'Now' */ }
    }
    return { value: Number(entry.seo_score) || 0, label };
  });

  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const delta = last && prev ? last.value - prev.value : 0;

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Current Score</span>
          <span className="text-xl font-mono font-black text-white">{last ? last.value : project?.overall_score ?? 0}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Audits Recorded</span>
          <span className="text-xl font-mono font-black text-blue-400">{points.length}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Last Change</span>
          <span className={`text-xl font-mono font-black flex items-center gap-1.5 ${
            delta > 0 ? 'text-green-400' : delta < 0 ? 'text-red-400' : 'text-gray-400'
          }`}>
            {delta > 0 ? <TrendingUp size={18} /> : delta < 0 ? <TrendingDown size={18} /> : <Minus size={18} />}
            {delta > 0 ? '+' : ''}{delta}
          </span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Trend Status</span>
          <span className={`text-sm font-bold ${delta > 0 ? 'text-green-400' : delta < 0 ? 'text-red-400' : 'text-gray-400'}`}>
            {delta > 0 ? 'Improving' : delta < 0 ? 'Declining' : 'Stable'}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-black/35 border border-gray-850 rounded-2xl p-5 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Activity size={15} className="text-blue-400" />
          <h4 className="font-extrabold text-white text-sm">On-Page Score Trend (30/60/90 day re-audits)</h4>
        </div>

        {error && (
          <div className="bg-red-950/30 border border-red-500/25 text-red-200 text-xs p-3 rounded-lg">
            Failed to load score history: {error}
          </div>
        )}

        <TrendChart points={points} />

        {points.length > 1 && (
          <p className="text-[11px] text-gray-500">
            Re-run the audit for this URL to append a new data point. Each re-audit of the same domain is tracked under
            this project so progress over 30/60/90 days is visible at a glance.
          </p>
        )}
      </div>

      {/* History table */}
      {(history || []).length > 0 && (
        <div className="bg-black/30 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm">Audit History Log</h4>
          <div className="border border-gray-800/80 rounded-xl overflow-hidden bg-black/20">
            <table className="w-full text-left border-collapse text-[11px]">
              <thead>
                <tr className="bg-gray-950/70 border-b border-gray-800 text-gray-400">
                  <th className="p-3 font-bold">Audit Date</th>
                  <th className="p-3 font-bold text-center">SEO Score</th>
                  <th className="p-3 font-bold text-center">Metadata</th>
                  <th className="p-3 font-bold text-center">Content</th>
                  <th className="p-3 font-bold text-center">Technical</th>
                  <th className="p-3 font-bold text-center">Integrity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-850">
                {(history || []).map((entry, idx) => {
                  const cats = entry.category_scores || {};
                  return (
                    <tr key={idx} className="hover:bg-gray-800/35 transition-colors">
                      <td className="p-3 font-mono text-gray-300">
                        {entry.date ? new Date(entry.date).toLocaleString() : 'Unknown'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`font-mono font-black ${entry.seo_score >= 80 ? 'text-green-400' : entry.seo_score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {entry.seo_score}
                        </span>
                      </td>
                      <td className="p-3 text-center font-mono text-gray-400">{cats.metadata ?? '—'}</td>
                      <td className="p-3 text-center font-mono text-gray-400">{cats.content_depth ?? '—'}</td>
                      <td className="p-3 text-center font-mono text-gray-400">{cats.technical ?? '—'}</td>
                      <td className="p-3 text-center font-mono text-gray-400">{cats.integrity ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendsPanel;
